import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Search, AlertTriangle, ChevronDown, ChevronRight,
  TrendingUp, TrendingDown, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// ========== RECONCILIATION TAB ==========
export function ReconciliationTab({ projectId }) {
  const [data, setData] = useState({ items: [], totals: {} });
  const [summary, setSummary] = useState({ sections: [] });
  const [loading, setLoading] = useState(false);
  const [showVarianceOnly, setShowVarianceOnly] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (projectId) params.append('project_id', projectId);
      if (showVarianceOnly) params.append('show_variance_only', 'true');

      const [reconRes, summaryRes] = await Promise.all([
        fetch(`${API}/api/boq/reconciliation?${params}`, { credentials: 'include' }),
        fetch(`${API}/api/boq/reconciliation/summary?${projectId ? `project_id=${projectId}` : ''}`, { credentials: 'include' }),
      ]);

      if (reconRes.ok) setData(await reconRes.json());
      if (summaryRes.ok) setSummary(await summaryRes.json());
    } catch (err) {
      console.error('Error fetching reconciliation:', err);
      toast.error('Failed to load reconciliation data');
    } finally {
      setLoading(false);
    }
  }, [projectId, showVarianceOnly]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const formatCurrency = (val) => `₹${(val || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  const formatQty = (val) => (val || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

  const itemsBySection = data.items.reduce((acc, item) => {
    const sec = item.section || 'Ungrouped';
    if (!acc[sec]) acc[sec] = [];
    acc[sec].push(item);
    return acc;
  }, {});

  const filteredSections = Object.keys(itemsBySection).filter(sec => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return sec.toLowerCase().includes(search) ||
      itemsBySection[sec].some(item =>
        (item.description || '').toLowerCase().includes(search) ||
        (item.item_code || '').toLowerCase().includes(search)
      );
  });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card><CardContent className="pt-4">
          <div className="text-[10px] font-display uppercase tracking-[0.16em] text-slate-500">Planned Value</div>
          <div className="text-xl font-display font-extrabold mt-1.5 tracking-tight text-slate-900">{formatCurrency(data.totals?.planned_value)}</div>
          <div className="text-xs text-slate-500 mt-0.5">{formatQty(data.totals?.planned_qty)} units</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-[10px] font-display uppercase tracking-[0.16em] text-slate-500">Committed Cost</div>
          <div className="text-xl font-display font-extrabold mt-1.5 tracking-tight text-slate-900">{formatCurrency(data.totals?.committed_cost)}</div>
          <div className="text-xs text-slate-500 mt-0.5">{formatQty(data.totals?.ordered_qty)} ordered</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-[10px] font-display uppercase tracking-[0.16em] text-slate-500">Received</div>
          <div className="text-xl font-display font-extrabold mt-1.5 tracking-tight text-emerald-700">{formatQty(data.totals?.received_qty)}</div>
          <div className="text-xs text-slate-500 mt-0.5">units received</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-[10px] font-display uppercase tracking-[0.16em] text-slate-500">Consumed</div>
          <div className="text-xl font-display font-extrabold mt-1.5 tracking-tight text-slate-900">{formatQty(data.totals?.consumed_qty)}</div>
          <div className="text-xs text-slate-500 mt-0.5">units consumed</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-[10px] font-display uppercase tracking-[0.16em] text-slate-500">Pending Order</div>
          <div className="text-xl font-display font-extrabold mt-1.5 tracking-tight text-amber-700">{formatQty(data.totals?.pending_order_qty)}</div>
          <div className="text-xs text-slate-500 mt-0.5">yet to order</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-[10px] font-display uppercase tracking-[0.16em] text-slate-500">Available Stock</div>
          <div className="text-xl font-display font-extrabold mt-1.5 tracking-tight text-slate-900">{formatQty(data.totals?.available_qty)}</div>
          <div className="text-xs text-slate-500 mt-0.5">in stores</div>
        </CardContent></Card>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Search by item code, description, or section..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
        </div>
        <Button variant={showVarianceOnly ? 'default' : 'outline'} onClick={() => setShowVarianceOnly(!showVarianceOnly)}
          className={showVarianceOnly ? 'bg-rose-600 hover:bg-rose-700' : ''}>
          <AlertTriangle className="w-4 h-4 mr-2" /> Show Variance Only
        </Button>
      </div>

      <Card>
        <CardHeader className="py-3"><CardTitle className="text-base">Section Summary</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto overflow-y-auto max-h-[70vh]">
            <table className="w-full text-sm sticky-col-header">
              <thead className="bg-gray-50 border-y">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Section</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-600">Items</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-600">Planned Value</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-600">Ordered %</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-600">Received %</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-600">Consumed %</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {summary.sections.map(sec => (
                  <tr key={sec.section} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-medium">{sec.section}</td>
                    <td className="px-4 py-2 text-right">{sec.items_count}</td>
                    <td className="px-4 py-2 text-right">{formatCurrency(sec.planned_value)}</td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-[#3b4872] rounded-full" style={{ width: `${Math.min(100, sec.order_pct)}%` }} />
                        </div>
                        <span className={sec.order_pct > 100 ? 'text-rose-700' : ''}>{sec.order_pct}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-emerald-600 rounded-full" style={{ width: `${Math.min(100, sec.receipt_pct)}%` }} />
                        </div>
                        <span>{sec.receipt_pct}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-slate-600 rounded-full" style={{ width: `${Math.min(100, sec.consume_pct)}%` }} />
                        </div>
                        <span className={sec.consume_pct > 100 ? 'text-rose-700' : ''}>{sec.consume_pct}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading reconciliation data...</div>
        ) : filteredSections.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {showVarianceOnly ? 'No variance items found' : 'No BOQ items found'}
          </div>
        ) : (
          filteredSections.map(section => {
            const items = itemsBySection[section].filter(item => {
              if (!searchTerm) return true;
              const search = searchTerm.toLowerCase();
              return (item.description || '').toLowerCase().includes(search) ||
                (item.item_code || '').toLowerCase().includes(search);
            });
            if (items.length === 0) return null;
            const isExpanded = expandedSections[section];
            const varianceCount = items.filter(i => i.has_variance).length;
            return (
              <Card key={section}>
                <CardHeader className="py-3 cursor-pointer hover:bg-gray-50" onClick={() => toggleSection(section)}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      <CardTitle className="text-base">{section}</CardTitle>
                      <span className="text-xs text-gray-500">({items.length} items)</span>
                      {varianceCount > 0 && (
                        <span className="text-xs bg-rose-50 text-rose-700 px-2 py-0.5 rounded-full">{varianceCount} variance</span>
                      )}
                    </div>
                  </div>
                </CardHeader>
                {isExpanded && (
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50 border-y">
                          <tr>
                            <th className="text-left px-3 py-2 font-medium text-gray-600">Item Code</th>
                            <th className="text-left px-3 py-2 font-medium text-gray-600">Description</th>
                            <th className="text-center px-3 py-2 font-medium text-gray-600">Unit</th>
                            <th className="text-right px-3 py-2 font-medium text-gray-600">Planned</th>
                            <th className="text-right px-3 py-2 font-medium text-gray-600">Ordered</th>
                            <th className="text-right px-3 py-2 font-medium text-gray-600">Received</th>
                            <th className="text-right px-3 py-2 font-medium text-gray-600">Consumed</th>
                            <th className="text-right px-3 py-2 font-medium text-gray-600">Available</th>
                            <th className="text-center px-3 py-2 font-medium text-gray-600">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {items.map(item => (
                            <tr key={item.id} className={`hover:bg-gray-50 ${item.has_variance ? 'bg-rose-50/40' : ''}`}>
                              <td className="px-3 py-2 font-mono">{item.item_code}</td>
                              <td className="px-3 py-2 max-w-xs truncate" title={item.description}>{item.description?.substring(0, 50)}...</td>
                              <td className="px-3 py-2 text-center">{item.unit}</td>
                              <td className="px-3 py-2 text-right">{formatQty(item.planned_qty)}</td>
                              <td className={`px-3 py-2 text-right ${item.over_ordered ? 'text-rose-700 font-medium' : ''}`}>
                                {formatQty(item.ordered_qty)}
                                {item.order_pct > 0 && <span className="text-gray-400 ml-1">({item.order_pct}%)</span>}
                              </td>
                              <td className={`px-3 py-2 text-right ${item.under_received ? 'text-amber-700' : ''}`}>
                                {formatQty(item.received_qty)}
                                {item.receipt_pct > 0 && <span className="text-gray-400 ml-1">({item.receipt_pct}%)</span>}
                              </td>
                              <td className={`px-3 py-2 text-right ${item.over_consumed ? 'text-rose-700 font-medium' : ''}`}>{formatQty(item.consumed_qty)}</td>
                              <td className="px-3 py-2 text-right font-medium text-slate-800">{formatQty(item.available_qty)}</td>
                              <td className="px-3 py-2 text-center">
                                {item.over_ordered && <span className="inline-flex items-center gap-1 text-rose-700" title="Over-ordered"><TrendingUp className="w-3 h-3" /></span>}
                                {item.under_received && <span className="inline-flex items-center gap-1 text-amber-700" title="Under-received"><TrendingDown className="w-3 h-3" /></span>}
                                {item.over_consumed && <span className="inline-flex items-center gap-1 text-rose-700" title="Over-consumed"><AlertTriangle className="w-3 h-3" /></span>}
                                {!item.has_variance && <span className="text-emerald-700"><CheckCircle2 className="w-3 h-3" /></span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}
